import React, { useEffect, useState } from "react";
import ClientLayout from "../../components/client/ClientLayout";
import RequireClientAdmin from "../../components/client/RequireClientAdmin";
import { useAuth } from "../../context/AuthContext";
import { clientApi } from "../../api";
import AdminSelect from "../../components/AdminSelect";
import { formatDateTimeAU } from "../../utils/formatDate";

export default function StaffManagement() {
  return (
    <ClientLayout>
      <RequireClientAdmin>
        <StaffInner />
      </RequireClientAdmin>
    </ClientLayout>
  );
}

function StaffInner() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "staff", password: "" });
  const [saving, setSaving] = useState(false);

  const [showPwModal, setShowPwModal] = useState(null); // user object
  const [newPw, setNewPw] = useState("");
  const [pwError, setPwError] = useState("");

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(""), 2500); };

  async function load() {
    setLoading(true); setError("");
    try { const d = await clientApi.getStaff(); setUsers(d.users || []); }
    catch (err) { setError(err.message); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function create(e) {
    e.preventDefault(); setSaving(true); setError("");
    try { await clientApi.createStaff(form); setShowAdd(false); setForm({ email: "", name: "", role: "staff", password: "" }); await load(); }
    catch (err) { setError(err.message); }
    setSaving(false);
  }

  async function changeRole(u, role) {
    setError("");
    try { await clientApi.updateStaff(u.id, { role }); await load(); }
    catch (err) { setError(err.message); }
  }

  async function toggleActive(u) {
    setError("");
    try { await clientApi.updateStaff(u.id, { active: !u.active }); await load(); }
    catch (err) { setError(err.message); }
  }

  async function del(u) {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    setError("");
    try { await clientApi.deleteStaff(u.id); flash(`${u.email} removed.`); await load(); }
    catch (err) { setError(err.message); }
  }

  async function resetPassword() {
    setPwError("");
    if (newPw.length < 6) return setPwError("Password must be at least 6 characters.");
    try { await clientApi.resetStaffPassword(showPwModal.id, newPw); setShowPwModal(null); setNewPw(""); flash("Password reset."); }
    catch (err) { setPwError(err.message); }
  }

  return (
    <>
      <div className="venues-page-header">
        <div>
          <h1>Staff Management</h1>
          <p className="page-subtitle">Invite and manage users for this venue</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Invite User</button>
      </div>

      {error && <div className="toast toast-error" style={{ marginBottom: "1rem" }}>{error}</div>}
      {success && <div className="toast toast-success" style={{ marginBottom: "1rem" }}>{success}</div>}

      {loading ? <div className="loading-state"><div className="spinner" /></div> : !users.length ? (
        <div className="empty-state">
          <h3>No users yet</h3>
          <p className="muted">Invite your first user to give your team access to this portal.</p>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Invite User</button>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>User</th><th>Role</th><th>Last login</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{u.name || "—"}</div>
                    <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{u.email}</div>
                  </td>
                  <td>
                    <AdminSelect
                      value={u.role}
                      onChange={(v) => changeRole(u, v)}
                      options={[{ value: "admin", label: "Admin" }, { value: "staff", label: "Staff" }]}
                      disabled={u.id === user.id}
                    />
                  </td>
                  <td style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>{formatDateTimeAU(u.last_login_at, "Never")}</td>
                  <td><span className={`badge ${u.active ? "active" : "inactive"}`}>{u.active ? "Active" : "Inactive"}</span></td>
                  <td>
                    <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                      <button className="btn btn-outline btn-sm" onClick={() => setShowPwModal(u)}>Reset password</button>
                      <button className="btn btn-outline btn-sm" onClick={() => toggleActive(u)} disabled={u.id === user.id}>{u.active ? "Deactivate" : "Activate"}</button>
                      <button className="btn btn-danger btn-sm" onClick={() => del(u)} disabled={u.id === user.id}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowAdd(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-header-text"><h2>Invite User</h2></div>
              <button className="modal-close-btn" onClick={() => setShowAdd(false)} aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <form onSubmit={create}>
              <div className="modal-body">
                <div className="form-field">
                  <label>Email <span className="required">*</span></label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                </div>
                <div className="form-field">
                  <label>Name</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="form-field">
                  <label>Role</label>
                  <AdminSelect value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={[{ value: "admin", label: "Admin (full control)" }, { value: "staff", label: "Staff (day-to-day only)" }]} />
                </div>
                <div className="form-field">
                  <label>Initial password <span className="required">*</span></label>
                  <input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" required />
                  <span className="field-hint">Share this with the user privately — they can change it after first login.</span>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowAdd(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Creating…" : "Create user"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPwModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowPwModal(null)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-header-text"><h2>Reset password for {showPwModal.email}</h2></div>
              <button className="modal-close-btn" onClick={() => setShowPwModal(null)} aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="modal-body">
              {pwError && <div className="toast toast-error" style={{ marginBottom: "0.75rem" }}>{pwError}</div>}
              <div className="form-field">
                <label>New password</label>
                <input type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} autoFocus />
                <span className="field-hint">At least 6 characters. Share it with the user privately.</span>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setShowPwModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={resetPassword}>Reset</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
